import {
  AlertCircle,
  AlertTriangle,
  Clock3,
  PawPrint,
  ScanSearch,
  ShieldCheck,
} from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  buildIdentitySectionModel,
  type IdentitySectionState,
} from "./identitySectionModel";

export type IdentitySectionProps = {
  summary: unknown;
  availabilityHint?: string | null;
  manifestStatusHint?: string | null;
  isAdmin?: boolean;
  onInspectIdentity?: (
    identityId: string,
    manifestVersion: number | null,
  ) => void;
};

const STATE_LABELS: Record<IdentitySectionState, string> = {
  ready: "Available",
  empty: "Analyzed / no dogs",
  pending: "Analysis pending",
  awaiting_confirmation: "Awaiting confirmation",
  partial: "Partial evidence",
  failed_safe: "Failed safe",
  not_applicable: "Disabled / not applicable",
  legacy: "Legacy / not recorded",
  deleted: "Evidence deleted",
  unavailable: "Unavailable",
  unknown: "Summary unavailable",
  invalid: "Unreadable summary",
};

const LABELS: Record<string, string> = {
  dog: "Dog",
  cat: "Cat",
  other: "Pet",
  unknown: "Subject",
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
  rejected: "Rejected",
  resolved: "Resolved",
  provisional: "Provisional",
  abstained: "Left unknown",
  user_confirmed: "User confirmed",
  operator_corrected: "Operator corrected",
};

function label(value: string) {
  return LABELS[value] ?? "Unknown";
}

function statusBadgeVariant(state: IdentitySectionState) {
  if (state === "failed_safe" || state === "invalid") return "destructive";
  if (
    state === "partial" ||
    state === "awaiting_confirmation" ||
    state === "unknown" ||
    state === "unavailable"
  ) {
    return "secondary";
  }
  return "outline";
}

function summaryAlert(state: IdentitySectionState) {
  if (state === "failed_safe") {
    return (
      <Alert variant="destructive">
        <AlertCircle aria-hidden="true" />
        <AlertTitle>Identity resolution failed safely</AlertTitle>
        <AlertDescription>
          Downstream generation must use identity-neutral targeting. No subject
          should be guessed from this result.
        </AlertDescription>
      </Alert>
    );
  }
  if (state === "invalid") {
    return (
      <Alert variant="destructive">
        <AlertCircle aria-hidden="true" />
        <AlertTitle>Identity summary could not be read safely</AlertTitle>
        <AlertDescription>
          The response was rejected at the compact privacy contract. No raw
          response fields are shown.
        </AlertDescription>
      </Alert>
    );
  }
  if (state === "awaiting_confirmation") {
    return (
      <Alert className="border-amber-300/60 bg-amber-50 dark:bg-amber-950/30">
        <Clock3 aria-hidden="true" />
        <AlertTitle>User confirmation is required</AlertTitle>
        <AlertDescription>
          The visual evidence is ambiguous. Until a valid answer is frozen,
          downstream generation must retain the conservative identity fallback.
        </AlertDescription>
      </Alert>
    );
  }
  if (state === "partial") {
    return (
      <Alert className="border-amber-300/60 bg-amber-50 dark:bg-amber-950/30">
        <AlertTriangle aria-hidden="true" />
        <AlertTitle>Identity evidence is partial</AlertTitle>
        <AlertDescription>
          Some assets or observations were unavailable. Treat every reported
          identity and downstream outcome within that limited coverage.
        </AlertDescription>
      </Alert>
    );
  }
  return null;
}

function inferenceAlert(inferenceExecuted: boolean | null) {
  if (inferenceExecuted !== false) return null;
  return (
    <Alert className="border-sky-300/60 bg-sky-50 dark:bg-sky-950/30">
      <ScanSearch aria-hidden="true" />
      <AlertTitle>Inference was not executed</AlertTitle>
      <AlertDescription>
        This is contract or synthetic projection evidence, not a detector/ReID
        result. It must not be used to claim that a dog was found.
      </AlertDescription>
    </Alert>
  );
}

function emptyStateMessage(state: IdentitySectionState) {
  const message: Record<string, string> = {
    empty:
      "Dog identity analysis completed with no dogs found. This is an analyzed zero, not missing data; unknown observations remain counted above.",
    pending:
      "Identity analysis has not reached a frozen result yet. The director and composer must not infer a stable identity from an incomplete summary.",
    awaiting_confirmation:
      "No frozen identity result is available while confirmation is pending.",
    partial: "The partial summary contains no displayable identity candidates.",
    failed_safe:
      "No identity candidates are usable from this failed-safe result.",
    not_applicable:
      "Identity resolution was intentionally not run for this Echo or product flow.",
    legacy:
      "Identity telemetry was not recorded for this legacy Echo. Its absence is unavailable evidence, not evidence that no pets appeared.",
    deleted:
      "Identity evidence was deleted under retention or erasure policy. It is not reconstructed from source media.",
    unavailable:
      "Identity metadata is unavailable for this Echo. This does not mean that analysis found zero pets.",
    unknown:
      "No identity summary was returned. The Echo may be legacy, not analyzed, or missing a projection; absence does not mean zero pets.",
    invalid:
      "No identity metadata can be displayed from the rejected response.",
  };
  return message[state] ?? "No identity candidates are available.";
}

export function IdentitySection({
  summary: rawSummary,
  availabilityHint,
  manifestStatusHint,
  isAdmin = false,
  onInspectIdentity,
}: IdentitySectionProps) {
  const model = buildIdentitySectionModel(rawSummary, {
    availabilityHint,
    manifestStatusHint,
  });
  const { state, summary, visibleSubjects } = model;

  return (
    <Card id="identity-section" aria-labelledby="identity-section-title">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle
              id="identity-section-title"
              className="flex items-center gap-2"
            >
              <PawPrint aria-hidden="true" className="size-4" />
              Identities
            </CardTitle>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Echo-local pet observation for diagnostics. It is metadata only
              and does not change direction, source targeting, rendering,
              quality checks, or delivery.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {summary?.manifest_version != null ? (
              <Badge variant="outline">
                Manifest v{summary.manifest_version}
                {summary.manifest_digest
                  ? ` / ${summary.manifest_digest.slice(7, 19)}`
                  : ""}
              </Badge>
            ) : null}
            {summary ? (
              <Badge variant="outline" className="capitalize">
                {summary.mode === "unknown"
                  ? "Mode not recorded"
                  : `${summary.mode} mode`}
              </Badge>
            ) : null}
            {summary ? (
              <Badge variant="outline">
                {summary.inference_executed === true
                  ? "Real ML inference"
                  : summary.inference_executed === false
                    ? "Inference not executed"
                    : "Inference provenance not recorded"}
              </Badge>
            ) : null}
            <Badge
              variant={statusBadgeVariant(state)}
              aria-label={`Identity status: ${STATE_LABELS[state]}`}
            >
              {STATE_LABELS[state]}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {summaryAlert(state)}
        {summary ? inferenceAlert(summary.inference_executed) : null}

        {summary ? (
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-md border p-3">
              <dt className="text-xs text-muted-foreground">
                Identity candidates
              </dt>
              <dd className="mt-1 text-xl font-semibold">
                {summary.subject_count}
              </dd>
            </div>
            <div className="rounded-md border p-3">
              <dt className="text-xs text-muted-foreground">
                Unknown observations
              </dt>
              <dd className="mt-1 text-xl font-semibold">
                {summary.unknown_instance_count}
              </dd>
            </div>
            <div className="rounded-md border p-3">
              <dt className="text-xs text-muted-foreground">Failed assets</dt>
              <dd className="mt-1 text-xl font-semibold">
                {summary.failed_asset_count}
              </dd>
            </div>
            <div className="rounded-md border p-3">
              <dt className="text-xs text-muted-foreground">Manifest state</dt>
              <dd className="mt-1 text-sm font-medium capitalize">
                {summary.manifest_status.replaceAll("_", " ")}
              </dd>
            </div>
          </dl>
        ) : null}

        {model.safeReasons.length > 0 ? (
          <div aria-label="Identity warnings" className="flex flex-wrap gap-2">
            {model.safeReasons.map((reason) => (
              <Badge key={reason} variant="secondary" className="capitalize">
                {reason}
              </Badge>
            ))}
          </div>
        ) : null}

        {visibleSubjects.length > 0 ? (
          <section aria-labelledby="identity-candidate-heading">
            <h3 id="identity-candidate-heading" className="text-sm font-medium">
              Failure-first identity rail
            </h3>
            <ol className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {visibleSubjects.map((subject, index) => (
                <li key={subject.echo_identity_id}>
                  <article className="h-full rounded-lg border p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <h4 className="font-medium">
                        {label(subject.species)} {index + 1}
                      </h4>
                      <Badge
                        variant={
                          subject.resolution_status === "abstained"
                            ? "destructive"
                            : "outline"
                        }
                      >
                        {label(subject.resolution_status)}
                      </Badge>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {label(subject.confidence)}
                    </p>
                    <dl className="mt-3 grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <dt className="text-xs text-muted-foreground">Media</dt>
                        <dd>{subject.distinct_media_count}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          Tracklets
                        </dt>
                        <dd>{subject.distinct_tracklet_count}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          References
                        </dt>
                        <dd>{subject.reference_count}</dd>
                      </div>
                    </dl>
                    {isAdmin && onInspectIdentity ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-4 w-full"
                        onClick={() =>
                          onInspectIdentity(
                            subject.echo_identity_id,
                            summary?.manifest_version ?? null,
                          )
                        }
                      >
                        <ScanSearch aria-hidden="true" className="size-3.5" />
                        Inspect identity evidence
                      </Button>
                    ) : null}
                  </article>
                </li>
              ))}
            </ol>
            {model.omittedSubjectCount > 0 ? (
              <p className="mt-3 text-xs text-muted-foreground">
                {model.omittedSubjectCount}{" "}
                {model.omittedSubjectCount === 1
                  ? "identity summary"
                  : "identity summaries"}{" "}
                omitted from this bounded rail.
              </p>
            ) : null}
          </section>
        ) : (
          <p className="text-sm text-muted-foreground">
            {emptyStateMessage(state)}
          </p>
        )}

        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck aria-hidden="true" className="size-3.5" />
          No names, pixels, storage locations, pairwise scores, or feature
          vectors are requested or rendered by this summary.
        </p>
      </CardContent>
    </Card>
  );
}
